/**
 * Ratification Page
 *
 * The ratification workspace — where governance authority finalizes priority
 * decisions. Ratification is permanent: once ratified, a priority list becomes
 * part of a Plan of Record and cannot be changed.
 */

import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BadgeCheck, Layers, Zap, Repeat, Infinity as InfinityIcon, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { usePrioritySetStore } from '@/stores/prioritization/prioritySetStore';
import { usePriorityListRatifyRights } from '@/hooks/prioritization/useActRight';

// ─── Cadence badge helpers ────────────────────────────────────────────────────

const CADENCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  episodic: Zap,
  periodic: Repeat,
  continuous: InfinityIcon,
};

const CADENCE_COLORS: Record<string, string> = {
  episodic: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  periodic: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  continuous: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
};

function CadenceBadge({ cadence }: { cadence?: string | Record<string, unknown> }) {
  // Resolve cadence to a plain mode string regardless of storage format
  // (JSONB may arrive as object, string, or stringified JSON)
  let key: string = 'periodic';
  if (typeof cadence === 'object' && cadence !== null) {
    key = ((cadence as Record<string, unknown>).mode as string) ?? 'periodic';
  } else if (typeof cadence === 'string') {
    const trimmed = cadence.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        key = (parsed.mode as string) ?? 'periodic';
      } catch {
        key = trimmed;
      }
    } else {
      key = trimmed || 'periodic';
    }
  }
  const Icon = CADENCE_ICONS[key] ?? Repeat;
  const color = CADENCE_COLORS[key] ?? 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)]';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <Icon className="h-3 w-3" />
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RatificationPage() {
  const [publishDate, setPublishDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const urlCycleId = searchParams.get('cycleId');
  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);
  const pinnedCycleName = usePinnedCycleStore((s) => s.pinnedCycleName);
  const activeCycleId = urlCycleId ?? pinnedCycleId;

  const cycles = useDegapProcessStore((state) => state.cycles);
  const tracks = useTrackStore((state) => state.tracks);
  const items = useItemsStore((state) => state.items);
  const userId = useUserStore((state) => state.userId);
  const userName = useUserStore((state) => state.userName);

  const loadPrioritySetsForCycle = usePrioritySetStore((s) => s.loadPrioritySetsForCycle);
  const ratifyPrioritySet = usePrioritySetStore((s) => s.ratifyPrioritySet);
  const prioritySets = usePrioritySetStore((s) => s.prioritySets);

  // Use active cycles as "approved" proxies — filter to active cycle if set
  const approvedCycles = useMemo(() => {
    const all = cycles.filter((c) => c.status === 'active');
    return activeCycleId ? all.filter((c) => c.id === activeCycleId) : all;
  }, [cycles, activeCycleId]);

  const activeCycleName = useMemo(
    () => cycles.find((c) => c.id === activeCycleId)?.name ?? pinnedCycleName ?? 'Selected cycle',
    [cycles, activeCycleId, pinnedCycleName],
  );

  // Preview items — top 3 from the store for demonstration
  const previewItems = useMemo(() => items.slice(0, 3), [items]);

  const selectedCycle = useMemo(
    () => (selectedCycleId ? approvedCycles.find((c) => c.id === selectedCycleId) ?? null : null),
    [selectedCycleId, approvedCycles],
  );

  // Load priority lists when a cycle is selected so we can ratify them
  useEffect(() => {
    if (selectedCycleId) {
      loadPrioritySetsForCycle(selectedCycleId);
      setSubmitResult(null);
    }
  }, [selectedCycleId, loadPrioritySetsForCycle]);

  const proposedSetsForCycle = useMemo(
    () => (selectedCycleId ? prioritySets.filter((s) => s.cycleId === selectedCycleId && s.state === 'proposed') : []),
    [prioritySets, selectedCycleId],
  );

  // D-4 (migration 987): every list this ratifies must be one the person may
  // ratify — today's rule, then the cycle's "Items / selection" decision right
  // when one is set. Otherwise Confirm is not drawn and the line says why (§2.8).
  const proposedSetIds = useMemo(() => proposedSetsForCycle.map((s) => s.id), [proposedSetsForCycle]);
  const ratifyRight = usePriorityListRatifyRights(proposedSetIds);
  const mayConfirm = proposedSetIds.length === 0 || ratifyRight.allowed;

  const handleRatify = async () => {
    if (!selectedCycle || !userId) return;
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      if (proposedSetsForCycle.length === 0) {
        // No priority lists in proposed status — emit a cycle-level governance event only
        const { getSafeSupabaseClient } = await import('@/lib/supabaseSync');
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          await supabase.from('governance_events').insert({
            id: crypto.randomUUID(),
            event_type: 'cycle_ratified',
            occurred_at: new Date().toISOString(),
            actor_id: userId,
            cycle_id: selectedCycle.id,
            note: JSON.stringify({
              ratifiedBy: userName,
              publishDate,
              notes: notes || null,
              source: 'ratification_page',
            }),
          });
        }
        setSubmitResult({
          ok: true,
          message: `${selectedCycle.name} has been ratified. No priority lists were in proposed status — governance event recorded.`,
        });
        setSelectedCycleId(null);
        return;
      }

      // Ratify each proposed priority list for the cycle
      const results = await Promise.all(
        proposedSetsForCycle.map((ps) =>
          ratifyPrioritySet(ps.id, userId, { publishDate, notes: notes || undefined }),
        ),
      );

      const failures = results.filter((r) => !r.success);
      if (failures.length > 0) {
        setSubmitResult({
          ok: false,
          message: `${failures.length} of ${results.length} priority list(s) could not be ratified: ${failures.map((f) => f.error).join('; ')}`,
        });
      } else {
        setSubmitResult({
          ok: true,
          message: `${selectedCycle.name} ratified — ${results.length} priority list${results.length > 1 ? 's' : ''} locked. Publish date: ${publishDate}.`,
        });
        setSelectedCycleId(null);
      }
    } catch (err) {
      setSubmitResult({ ok: false, message: String(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturn = () => {
    setSelectedCycleId(null);
    setSubmitResult(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<BadgeCheck className="h-4 w-4 text-[var(--green)]" />}
        iconBg="bg-[var(--green-dim)]"
        title="Ratification"
        subtitle="Final governance approval that locks a priority list and sets its publish date."
      />

      <div className="space-y-6">
        {/* Active cycle filter chip */}
        {activeCycleId && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
              Filtered to: <span className="font-semibold">{activeCycleName}</span>
              <button
                type="button"
                onClick={() => {
                  setSearchParams((p) => { p.delete('cycleId'); return p; });
                  usePinnedCycleStore.getState().exitWorkspace();
                }}
                className="ml-1 text-[var(--mustard)] hover:text-[var(--mustard)] transition-colors"
                title="Show all cycles"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
            <Link to="/prioritization/governance/ratification" className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              Show all cycles
            </Link>
          </div>
        )}

        {/* ─── Explainer banner ───────────────────────────────────────────── */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
          <p className="text-sm text-[var(--text-sec)] leading-relaxed">
            <span className="font-semibold">Approved → Ratified (locked) → Published.</span>
            {' '}Ratification is permanent. Once ratified, a priority list is written to the cycle report and
            cannot be changed. Set a publish date to control when it becomes visible to the organization.
          </p>
        </div>

        {/* ─── Main content ───────────────────────────────────────────────── */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6 space-y-6 lg:space-y-0">
          {/* ─── Left: Ready for ratification ─────────────────────────────── */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
              Ready for Ratification
            </h2>

            {approvedCycles.length === 0 ? (
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-10 text-center">
                <p className="text-sm text-[var(--text-ter)]">
                  No proposals are currently approved for ratification.{' '}
                  <Link
                    to="/prioritization/governance/proposals"
                    className="text-[var(--text-ter)] hover:underline"
                  >
                    View Proposals
                  </Link>
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {approvedCycles.map((cycle) => {
                  const cycleTrackCount = tracks.filter((t) => t.cycleId === cycle.id).length;
                  const isSelected = selectedCycleId === cycle.id;

                  return (
                    <div
                      key={cycle.id}
                      className={`bg-[var(--bg-raised)]  border transition-colors ${
                        isSelected ? 'border-[var(--green)] ring-1 ring-green-300' : 'border-[var(--rule)]'
                      }`}
                    >
                      <div className="p-5">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-[var(--text-pri)]">
                                {cycle.name}
                              </span>
                              <CadenceBadge cadence={cycle.cadence} />
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--slate-dim)] text-[var(--text-sec)]">
                                Approved
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-[var(--text-ter)] mt-1">
                              <span className="flex items-center gap-1">
                                <Layers className="h-3.5 w-3.5" />
                                {cycleTrackCount} track{cycleTrackCount !== 1 ? 's' : ''}
                              </span>
                              <span>Approved: Pending</span>
                              <span>Submitted by: —</span>
                            </div>
                          </div>
                        </div>

                        {/* Ranked list preview */}
                        {previewItems.length > 0 && (
                          <div className="bg-[var(--surface)] p-3 mb-3">
                            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">
                              Top items preview
                            </p>
                            <ol className="space-y-1">
                              {previewItems.map((item, i) => (
                                <li key={item.id} className="flex items-center gap-2 text-xs text-[var(--text-sec)]">
                                  <span className="w-4 h-4 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                    {i + 1}
                                  </span>
                                  <span className="truncate">
                                    {(item as { title?: string }).title ?? 'Untitled item'}
                                  </span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Action */}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedCycleId(isSelected ? null : cycle.id)
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium  border transition-colors ${
                            isSelected
                              ? 'bg-[var(--green)] text-white border-[var(--green)]'
                              : 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--bg-raised)] border-[var(--green)] hover:bg-[var(--green-dim)]'
                          }`}
                        >
                          <BadgeCheck className="h-3.5 w-3.5" />
                          {isSelected ? 'Selected for Ratification' : 'Ratify'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Right: Ratification details panel ────────────────────────── */}
          <div className="lg:col-span-1">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-4">
              Ratification Details
            </h2>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 space-y-4">
              {/* Ratified by (read-only) */}
              <div>
                <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
                  Ratified By
                </label>
                <div className="px-3 py-2 bg-[var(--surface)] border border-[var(--rule)] text-sm text-[var(--text-sec)] cursor-not-allowed">
                  {userName || 'Current User'}
                </div>
              </div>

              {/* Ratification date (read-only) */}
              <div>
                <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
                  Ratification Date
                </label>
                <div className="px-3 py-2 bg-[var(--surface)] border border-[var(--rule)] text-sm text-[var(--text-sec)] cursor-not-allowed">
                  {todayIso()}
                </div>
              </div>

              {/* Publish date (editable) */}
              <div>
                <label
                  htmlFor="publish-date"
                  className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5"
                >
                  Publish Date
                </label>
                <input
                  id="publish-date"
                  type="date"
                  value={publishDate}
                  onChange={(e) => setPublishDate(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                />
              </div>

              {/* Notes */}
              <div>
                <label
                  htmlFor="ratification-notes"
                  className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5"
                >
                  Notes
                </label>
                <textarea
                  id="ratification-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional ratification notes..."
                  className="w-full px-3 py-2 border border-[var(--rule)] text-sm text-[var(--text-pri)] resize-none focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                />
              </div>

              {/* Result banner */}
              {submitResult && (
                <div className={`flex items-start gap-2  px-3 py-2.5 text-sm ${
                  submitResult.ok
                    ? 'bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
                    : 'bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)]'
                }`}>
                  {submitResult.ok
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[var(--green)]" />
                    : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-[var(--coral)]" />}
                  <span>{submitResult.message}</span>
                </div>
              )}

              {/* Buttons */}
              <div className="space-y-2 pt-1">
                {!mayConfirm && ratifyRight.line && (
                  <p className="text-sm text-[var(--text-ter)]">{ratifyRight.line}</p>
                )}
                {mayConfirm && (
                  <button
                    type="button"
                    disabled={!selectedCycle || isSubmitting}
                    onClick={handleRatify}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--green)] text-white text-sm font-semibold hover:bg-[var(--green)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting
                      ? <><Loader2 className="h-4 w-4 animate-spin" /> Ratifying…</>
                      : <><BadgeCheck className="h-4 w-4" /> Confirm Ratification</>
                    }
                  </button>
                )}
                <button
                  type="button"
                  disabled={!selectedCycle || isSubmitting}
                  onClick={handleReturn}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-[var(--coral)] text-xs font-medium border border-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Return for Revision
                </button>
              </div>

              {/* Warning note */}
              <p className="text-xs text-[var(--text-ter)] text-center pt-1">
                Ratification is permanent and cannot be undone.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
